import { useCallback, useEffect, useRef, useState } from 'react';
import { notificationsAPI, type NotificationItem } from '../services/api';

const POLL_INTERVAL_MS = 60_000;

interface UseNotificationsState {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  markRead: (n: NotificationItem) => Promise<void>;
  markAllRead: () => Promise<void>;
}

/**
 * Polls /notifications every 60s + on mount. Exposes mark-read helpers
 * that optimistically update local state before the API confirms.
 *
 * Stops polling when the tab is hidden (Page Visibility API) so we don't
 * burn requests for a backgrounded tab; resumes on visibility change.
 */
export function useNotifications(enabled: boolean = true): UseNotificationsState {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!enabled || cancelledRef.current) return;
    setLoading(true);
    try {
      const data = await notificationsAPI.getMine({ limit: 50 });
      if (cancelledRef.current) return;
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
      setError(null);
    } catch (err: any) {
      if (cancelledRef.current) return;
      setError(err?.response?.data?.message || 'Failed to load notifications');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [enabled]);

  const schedule = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(async () => {
      if (document.visibilityState === 'visible') await fetchOnce();
      schedule();
    }, POLL_INTERVAL_MS);
  }, [fetchOnce]);

  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;
    fetchOnce();
    schedule();

    // Refetch the moment the tab becomes visible again (handles long
    // backgrounded periods where the timer fired without a request).
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchOnce();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelledRef.current = true;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, fetchOnce, schedule]);

  const markRead = useCallback(async (n: NotificationItem) => {
    if (n.readAt) return; // already read
    setNotifications((prev) =>
      prev.map((x) => (x.notifId === n.notifId ? { ...x, readAt: new Date().toISOString() } : x)),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await notificationsAPI.markRead(n.notifId, n.createdAt);
    } catch {
      // Best-effort: on failure, the next poll will re-sync.
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    setUnreadCount(0);
    try {
      await notificationsAPI.markAllRead();
    } catch {
      // Best-effort: next poll re-syncs if the server didn't catch it.
    }
  }, []);

  return { notifications, unreadCount, loading, error, refetch: fetchOnce, markRead, markAllRead };
}
